class WidgetRegistry {
    static register(ctor, cssKey, propResolver, propScheme, alias) {
        const name = (alias || inferName(ctor))?.trim();
        if (!name)
            throw new Error("[WidgetRegistry] Нельзя зарегистрировать виджет без имени");
        const css = cssKey?.replace(/^\./, "");
        const prev = this.defs.get(name);
        const def = {
            name,
            cssKey: css ?? prev?.cssKey,
            ctor,
            propResolver: propResolver ?? prev?.propResolver,
            propScheme: propScheme,
            loader: undefined, // после регистрации считаем загруженным
            _inflight: undefined
        };
        this.defs.set(name, def);
        if (def.cssKey)
            this.byCss.set(def.cssKey, def);
        this.byCtor.set(ctor, def);
        this.snapshotCache = null;
    }
    /** Ленивая регистрация: знаем name (+/- cssKey), но ctor появится позднее */
    static registerLazy(name, loader, cssKey) {
        const css = cssKey?.replace(/^\./, "");
        const prev = this.defs.get(name);
        const def = {
            name,
            cssKey: css ?? prev?.cssKey,
            ctor: prev?.ctor, // вдруг уже была жадная регистрация
            propResolver: prev?.propResolver,
            propScheme: prev?.propScheme,
            loader,
            _inflight: undefined
        };
        this.defs.set(name, def);
        if (def.cssKey)
            this.byCss.set(def.cssKey, def);
        this.snapshotCache = null;
    }
    /** Принудительно загрузить (по имени ИЛИ по cssKey) */
    static async ensureLoaded(nameOrCss) {
        const css = nameOrCss.replace(/^\./, "");
        // 1) найти def
        let def = this.defs.get(nameOrCss) ?? this.byCss.get(css);
        if (!def)
            return undefined;
        // 2) если уже загружен — отдать
        if (def.ctor)
            return def;
        // 3) если нет лоадера — нечего грузить
        if (!def.loader)
            return undefined;
        // 4) дедупликация гонок
        if (!def._inflight) {
            def._inflight = (async () => {
                const loaded = await def.loader();
                // применить результаты
                def.ctor = loaded.ctor;
                // регистрация провайдеров команд виджета
                const ctorAny = loaded.ctor;
                if (typeof ctorAny.registerCommandProviders === "function") {
                    ctorAny.registerCommandProviders();
                }
                // подтянуть optionScheme из ctor, если не задано ранее
                if (!def.propScheme && typeof ctorAny._optionScheme === "function") {
                    def.propScheme = ctorAny._optionScheme();
                }
                // подтянуть propResolver по той же логике (симметрично)
                if (!def.propResolver && typeof ctorAny._propResolver === "function") {
                    def.propResolver = ctorAny._propResolver();
                }
                // после успеха лоадер больше не нужен
                def.loader = undefined;
                this.byCtor.set(loaded.ctor, def);
                this.snapshotCache = null;
            })().catch(err => {
                // при ошибке позволяем повторить загрузку позже
                def._inflight = undefined;
                throw err;
            });
        }
        await def._inflight;
        def._inflight = undefined;
        return def;
    }
    static get(name) { return this.defs.get(name); }
    static getByCss(cssKey) { return this.byCss.get(cssKey.replace(/^\./, "")); }
    static all() {
        if (!this.snapshotCache)
            this.snapshotCache = Array.from(this.defs.values());
        return this.snapshotCache;
    }
    /** Сервис: список cssKey ленивых (не загруженных) виджетов — для селектора */
    static listLazyCss() {
        const out = [];
        for (const def of this.defs.values()) {
            if (!def.ctor && def.cssKey)
                out.push(def.cssKey);
        }
        return out;
    }
    /**
     * Возвращает `WidgetDef` по конструктору (ctor) виджета.
     * Не инициирует загрузку; O(1).
     * @param ctor Конструктор виджета.
     * @returns Описание или `undefined`, если ctor не зарегистрирован.
     */
    static getByCtor(ctor) {
        return this.byCtor.get(ctor);
    }
}
/** все определения по имени */
WidgetRegistry.defs = new Map();
/** индекс по cssKey (если задан) */
WidgetRegistry.byCss = new Map();
WidgetRegistry.snapshotCache = null;
WidgetRegistry.byCtor = new WeakMap();
function inferName(ctor) {
    const raw = ctor?.name?.trim();
    if (!raw)
        return undefined;
    return raw.replace(/Widget$/, "");
}

export { WidgetRegistry };
